/**
 * Ponto fixo Q8 — a representação numérica do estado da simulação.
 *
 * §5.3 permite `+ - * /` em double (são exatos por IEEE-754) mas manda preferir
 * inteiro sempre que der. Aqui "der" é praticamente sempre: posição, velocidade
 * e combustível cabem folgados em Q8, e inteiro elimina de vez a classe de bug
 * em que dois motores acumulam erro de arredondamento de formas diferentes ao
 * longo de 18 mil ticks.
 *
 * 1 unidade = 1/256 px. Faixa útil: ±8,3 milhões de px — o rio inteiro de uma
 * run de uma hora cabe com folga.
 */
export const FP_SHIFT = 8;
export const FP_ONE = 1 << FP_SHIFT; // 256
/** px (número inteiro ou fracionário) → ponto fixo. Só em constantes de config. */
export function fp(pixels) {
    return Math.round(pixels * FP_ONE) | 0;
}
/** Ponto fixo → px inteiro, truncando na direção de zero. Para render e colisão. */
export function fpToPx(value) {
    return (value / FP_ONE) | 0;
}
/** Ponto fixo → px arredondado ao inteiro mais próximo. Para posicionar sprite. */
export function fpRoundPx(value) {
    return Math.round(value / FP_ONE) | 0;
}
/** Multiplica dois valores em ponto fixo. */
export function fpMul(a, b) {
    return Math.round((a * b) / FP_ONE) | 0;
}
/** Divide dois valores em ponto fixo. */
export function fpDiv(a, b) {
    if (b === 0)
        return 0;
    return Math.round((a * FP_ONE) / b) | 0;
}
/** Prende um valor à faixa `[min, max]`. */
export function clamp(value, min, max) {
    if (value < min)
        return min;
    if (value > max)
        return max;
    return value;
}
/**
 * Aproximação de um passo de suavização exponencial, em inteiro.
 *
 * Substitui o `lerp` com `Math.exp(-dt/τ)` que a spec descreve em prosa (§7.1):
 * a exponencial é proibida no core. `alphaNum/alphaDen` é a fração do caminho
 * percorrida por tick, pré-calculada em `config.ts` a partir de τ.
 */
export function approach(current, target, alphaNum, alphaDen) {
    const delta = target - current;
    if (delta === 0)
        return current;
    const step = Math.round((delta * alphaNum) / alphaDen) | 0;
    // Garante progresso: sem isto, deltas pequenos arredondam para 0 e o valor
    // trava para sempre a um passo do alvo.
    if (step === 0)
        return delta > 0 ? current + 1 : current - 1;
    return current + step;
}
//# sourceMappingURL=fixed.js.map